/**
 * "Create driver" — admin makes a driver account directly (server-side, no
 * email-link round-trip, no local script). Returns hand-over credentials.
 * Self-contained: renders its own button + modal.
 */
import React, { useState } from "react";
import { createDriver, CreatedDriver } from "../services/driversApi";

interface BusLite { id: string; name: string; plate: string }

export default function CreateDriverButton({ orgCode, buses }: { orgCode: string; buses: BusLite[] }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busId, setBusId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreatedDriver | null>(null);

  function reset() { setName(""); setEmail(""); setBusId(""); setError(null); setResult(null); setBusy(false); }
  function close() { setOpen(false); reset(); }

  async function submit() {
    if (!name.trim() || !email.trim()) { setError("Enter the driver's name and email."); return; }
    setBusy(true); setError(null);
    try {
      const r = await createDriver({ orgCode, email: email.trim(), displayName: name.trim(), busId: busId || undefined });
      setResult(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const copy = (t: string) => { try { navigator.clipboard?.writeText(t); } catch { /* ignore */ } };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 20, border: "1px solid #cbd5e1", cursor: "pointer", background: "#fff", color: "#0f172a", fontFamily: "Geist, sans-serif", fontSize: 13, fontWeight: 600 }}
      >
        + Create driver
      </button>

      {open && (
        <div onClick={close} style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(15,23,42,.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 440, background: "#fff", borderRadius: 16, padding: 22, fontFamily: "Geist, sans-serif" }}>
            {!result ? (
              <>
                <div style={{ fontSize: 17, fontWeight: 800, color: "#0f172a" }}>Create a driver account</div>
                <div style={{ fontSize: 12.5, color: "#94a3b8", margin: "4px 0 16px" }}>The account is created on the server — no email link needed. You'll get a password to hand over.</div>
                {error && <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 10 }}>{error}</div>}
                <Field label="Driver name"><input value={name} onChange={(e) => setName(e.target.value)} style={inp} placeholder="e.g. Peter Otieno" /></Field>
                <Field label="Email (their login)"><input value={email} onChange={(e) => setEmail(e.target.value)} style={inp} placeholder="driver@example.com" /></Field>
                <Field label="Assign to bus (optional)">
                  <select value={busId} onChange={(e) => setBusId(e.target.value)} style={inp}>
                    <option value="">— none —</option>
                    {buses.map((b) => (
                          /* "Bus KCE999Z · KCE999Z" — a bus with no nickname is
                             NAMED after its plate, so printing both said the
                             same thing twice (Gabriel, 09-07). Show the plate
                             separately only when the name is not already it. */
                          <option key={b.id} value={b.id}>
                            {b.name && b.plate && !b.name.includes(b.plate)
                              ? `${b.name} · ${b.plate}`
                              : (b.name || b.plate)}
                          </option>
                        ))}
                  </select>
                </Field>
                <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
                  <button onClick={submit} disabled={busy} style={{ ...btn, background: "#2563eb", color: "#fff", flex: 1 }}>{busy ? "Creating…" : "Create account"}</button>
                  <button onClick={close} disabled={busy} style={{ ...btn, background: "#e2e8f0", color: "#475569" }}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 17, fontWeight: 800, color: "#0f172a" }}>{result.created ? "Driver account created" : "Driver already existed"}</div>
                <div style={{ fontSize: 12.5, color: "#94a3b8", margin: "4px 0 14px" }}>
                  {result.created ? "Hand these over in person — the password is shown once." : "An account with this email already existed; it's now linked to this org."}
                </div>
                <Cred label="Email" value={result.email} onCopy={() => copy(result.email)} />
                {result.password && <Cred label="Password" value={result.password} pw onCopy={() => copy(result.password!)} />}
                {/* 2026-09-16: the bus link used to fail silently. The trip
                    routes authorise a driver from the BUS's roster alone, so a
                    driver who never landed on it is refused on every milestone
                    and cannot cancel — while this screen said all was well. */}
                {result.busLinked === false && (
                  <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", borderRadius: 10, padding: "10px 12px", fontSize: 12.5, marginBottom: 8 }}>
                    The account is ready, but we could not put {result.email} on that bus. Assign the bus again from Accounts — until then they cannot send trip updates or cancel a trip.
                  </div>
                )}
                <button onClick={close} style={{ ...btn, background: "#2563eb", color: "#fff", width: "100%", marginTop: 16 }}>Done</button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const inp: React.CSSProperties = { width: "100%", border: "1px solid #cbd5e1", borderRadius: 10, padding: "10px 12px", fontSize: 14, boxSizing: "border-box", fontFamily: "inherit" };
const btn: React.CSSProperties = { border: "none", borderRadius: 10, padding: "11px 16px", fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: "#64748b", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</div>
      {children}
    </div>
  );
}

function Cred({ label, value, pw, onCopy }: { label: string; value: string; pw?: boolean; onCopy: () => void }) {
  return (
    <div style={{ background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: 10, padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
      <div>
        <div style={{ fontSize: 10.5, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>{label}</div>
        <div style={{ fontFamily: "monospace", fontSize: 14, color: pw ? "#2563eb" : "#0f172a" }}>{value}</div>
      </div>
      <button onClick={onCopy} style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 12 }}>Copy</button>
    </div>
  );
}
